/*
  Lab Results — certificate list behaviour (sections/labresults-list.liquid).

  Progressive enhancement. The server renders every row, each row's details
  panel open, and a hidden "Load more" button. Without JavaScript everything
  is visible. With it:
    - rows past the first `data-step` are hidden behind "Load more";
    - each details panel collapses behind a "Details" toggle in its row;
    - "Download (PDF)" gains the file size, read from a HEAD request (the CDN
      sends Content-Length and allows cross-origin reads). Sizes are fetched
      only for rows that are showing.
  Focus moves to the first newly shown row after "Load more", so keyboard
  and screen reader users land on the new content.
*/
if (!customElements.get('lab-results-list')) {
  const sizeCache = new Map();

  const formatSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const fetchSize = (url) => {
    if (!sizeCache.has(url)) {
      sizeCache.set(
        url,
        fetch(url, { method: 'HEAD' })
          .then((res) => {
            const length = Number(res.headers.get('content-length'));
            return res.ok && length > 0 ? formatSize(length) : null;
          })
          .catch(() => null)
      );
    }
    return sizeCache.get(url);
  };

  customElements.define(
    'lab-results-list',
    class LabResultsList extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-lr-row]'));
        this.button = this.querySelector('[data-lr-more]');
        this.shownLabel = this.querySelector('[data-lr-shown]');
        this.step = parseInt(this.dataset.step, 10) || 10;

        this.rows.forEach((row) => this.setUpToggle(row));

        if (this.button && this.rows.length > this.step) {
          this.onMore = this.showMore.bind(this);
          this.button.addEventListener('click', this.onMore);
          this.render(this.step);
        } else {
          this.render(this.rows.length);
        }
      }

      disconnectedCallback() {
        if (this.button && this.onMore) this.button.removeEventListener('click', this.onMore);
      }

      setUpToggle(row) {
        const toggle = row.querySelector('[data-lr-toggle]');
        const panel = row.querySelector('[data-lr-panel]');
        const divider = row.querySelector('[data-lr-toggle-divider]');
        if (!toggle || !panel) return;

        panel.hidden = true;
        toggle.hidden = false;
        if (divider) divider.hidden = false;
        toggle.addEventListener('click', () => {
          const open = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
        });
      }

      showMore() {
        const firstNew = this.visible;
        this.render(this.visible + this.step);
        const link = this.rows[firstNew] && this.rows[firstNew].querySelector('a');
        if (link) link.focus();
      }

      render(count) {
        this.visible = Math.min(count, this.rows.length);
        this.rows.forEach((row, i) => {
          row.hidden = i >= this.visible;
          if (!row.hidden) this.fillSize(row);
        });
        if (this.button) this.button.hidden = this.visible >= this.rows.length;
        if (this.shownLabel) this.shownLabel.textContent = this.visible;
      }

      fillSize(row) {
        const link = row.querySelector('[data-lr-download]');
        const slot = row.querySelector('[data-lr-size]');
        if (!link || !slot || slot.dataset.done) return;
        slot.dataset.done = 'true';
        fetchSize(link.href).then((size) => {
          if (size) slot.textContent = ` — ${size}`;
        });
      }
    }
  );
}
